import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocalizedStrings } from "../Localization";
import { Card, CardHeader, CardBody, CardFooter, CodeDisplay, DateDisplay } from "@aidkitorg/component-library";
import { useDebouncedSearch, HistorySearchBar, HistoryFilterOptions, HistoryListWrapper, filterEmptyErrors, highlightText, getPrettyValue } from "./historyUtils";

type UnsubmittedHistoryProps = {
  info: Record<string, any>;
};

type UnsubmittedHistoryItem = {
  key: string;
  value: any;
  created_at?: string;
  latest: boolean;
};

type UnsubmittedHistoryItemProps = {
  item: UnsubmittedHistoryItem;
  searchTerm: string;
};

function UnsubmittedHistoryItemComponent(props: UnsubmittedHistoryItemProps) {
  const L = useLocalizedStrings();
  const { pretty, isJSON } = useMemo(() => getPrettyValue(props.item.value), [props.item.value]);

  return (
    <Card className="p-3">
      <CardHeader
        title={<strong>{highlightText(props.item.key, props.searchTerm)}</strong>}
        badgeText={props.item.latest ? L.applicant.history.latest : undefined}
        badgeVariant="success"
      />
      <CardBody className="py-2">
        <div>
          <CodeDisplay
            className='border-0'
            variant="gray"
            contentMode="wrap"
            language={isJSON ? "json" : "text"}
            code={pretty}
            highlightText={props.searchTerm}
            copyable />
        </div>
      </CardBody>
      <CardFooter>
        <div className="text-sm flex justify-end flex-col items-end">
          <div>
            {props.item.created_at && (
              <DateDisplay variant='datetime' utcValue={props.item.created_at} />
            )}
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}

export function UnsubmittedHistory(props: UnsubmittedHistoryProps) {
  const L = useLocalizedStrings();
  const [searchInput, setSearchInput] = useState('');
  const searchTerm = useDebouncedSearch(searchInput, 200);
  const [useUTC, setUseUTC] = useState(false);
  const onCheckedChange = useCallback((checked: boolean) => setUseUTC(checked), []);
  const [showSearchOptions, setShowSearchOptions] = useState(false);
  const [exactMatch, setExactMatch] = useState(false);
  const [searchTargetField, setSearchTargetField] = useState(true);
  const [searchValue, setSearchValue] = useState(true);
  const [showEmptyFields, setShowEmptyFields] = useState(true);
  const [hideEmptyErrors, setHideEmptyErrors] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredHistory, setFilteredHistory] = useState<UnsubmittedHistoryItem[]>([]);

  const historyItems = useMemo(() => {
    const dataFields = Object.keys(props.info).filter(
      key => !key.endsWith('_created_at')
    );

    return dataFields.map(key => ({
      key,
      value: props.info[key],
      created_at: props.info[key + '_created_at'],
      latest: true
    }));
  }, [props.info]);

  useEffect(() => {
    if (historyItems) {
      let filtered = historyItems;

      if (searchTerm) {
        filtered = filtered.filter(item => {
          const searchLower = searchTerm.toLowerCase();
          const keyMatch = searchTargetField && (
            exactMatch
              ? item.key.toLowerCase() === searchLower
              : item.key.toLowerCase().includes(searchLower)
          );
          const valueMatch = searchValue && (
            exactMatch
              ? String(item.value ?? '').toLowerCase() === searchLower
              : String(item.value ?? '').toLowerCase().includes(searchLower)
          );
          return keyMatch || valueMatch;
        });
      }

      if (startDate || endDate) {
        filtered = filtered.filter(item => {
          if (!item.created_at) return false;
          const itemDate = new Date(item.created_at);
          if (startDate && itemDate < new Date(startDate)) return false;
          if (endDate && itemDate > new Date(new Date(endDate).setHours(23, 59, 59, 999))) return false;
          return true;
        });
      }

      let finalFiltered = filterEmptyErrors(filtered, historyItems, hideEmptyErrors);
      finalFiltered = showEmptyFields ? finalFiltered : finalFiltered.filter((historyItem: UnsubmittedHistoryItem) => Boolean(historyItem.value));
      setFilteredHistory(finalFiltered);
    }
  }, [historyItems, searchTerm, exactMatch, searchTargetField, searchValue, showEmptyFields, hideEmptyErrors, startDate, endDate]);

  return (
    <div>
      <HistorySearchBar
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        searchTerm={searchTerm}
        showSearchOptions={showSearchOptions}
        setShowSearchOptions={setShowSearchOptions}
        L={L}
      />
      {showSearchOptions && (
        <HistoryFilterOptions
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          exactMatch={exactMatch}
          setExactMatch={setExactMatch}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          searchTargetField={searchTargetField}
          setSearchTargetField={setSearchTargetField}
          showEmptyFields={showEmptyFields}
          setShowEmptyFields={setShowEmptyFields}
          hideEmptyErrors={hideEmptyErrors}
          setHideEmptyErrors={setHideEmptyErrors}
          L={L}
        />
      )}
      <HistoryListWrapper
        useUTC={useUTC}
        onCheckedChange={onCheckedChange}
      >
        {filteredHistory.map((item: UnsubmittedHistoryItem, i: number) => (
          <UnsubmittedHistoryItemComponent key={`history_${item.key}_${i}`} item={item} searchTerm={searchTerm} />
        ))}
      </HistoryListWrapper>
    </div>
  );
}
